import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
    CandlestickSeries,
    ColorType,
    LineSeries,
    LineStyle,
    createChart,
    createSeriesMarkers,
    type ISeriesApi,
    type SeriesType,
    type Time,
    type UTCTimestamp
} from 'lightweight-charts';
import {
    microtrendComparisonConfigSchema,
    microtrendComparisonDurationSchema,
    MICROTREND_RESEARCH_PRESETS,
    MICROTREND_RESEARCH_PRESET_VERSION,
    type MicrotrendComparisonConfig,
    type MicrotrendComparisonResult,
    type MicrotrendComparisonSnapshot,
    type MicrotrendPersistedResearchImpulse
} from '@einhaus/project314-types';
import { getDateFromUnix } from '@einhaus/meteoric-helpers';
import { trpc, trpcUnbatchedClient } from '@/api/trpc';
import { useAppQuery } from '@/hooks/useAppQuery';

import type { ChartSettings } from './ChartContainer';
import MicrotrendObservationPanel from './MicrotrendObservationPanel';
import {
    currentMicrotrendComparisonResult,
    microtrendComparisonRows,
    microtrendComparisonBarClose,
    type MicrotrendComparisonMode,
    type MicrotrendComparisonRow
} from './microtrendComparisonDisplay';

const initialConfig: MicrotrendComparisonConfig = MICROTREND_RESEARCH_PRESETS[1].config;
// Preserve Unix time ordering across DST; format labels in ET without shifting timestamps.
const chartTime = (time: number): UTCTimestamp => time as UTCTimestamp;
const timeLabel = (value: number) => getDateFromUnix(value, 'ymdhms');
const numberLabel = (value: number | null | undefined, digits = 3) => (value == null ? 'Unavailable' : value.toFixed(digits));
const controlClass = 'rounded border border-zinc-600 bg-zinc-900 px-3 py-2 text-sm text-white disabled:opacity-40';

interface MicrotrendComparisonPanelProps {
    settings: ChartSettings;
    onUseRecentWindow: (() => void) | undefined;
}

export default function MicrotrendComparisonPanel({ settings, onUseRecentWindow }: MicrotrendComparisonPanelProps) {
    const duration = microtrendComparisonDurationSchema.safeParse(settings.duration);
    if (!duration.success || settings.option)
        return (
            <p className="p-4 text-amber-300">
                Microtrend comparison supports stock, index, and crypto charts at 1, 3, 5, 15, 30, 60, 120, 180, or 240 minutes and one day.
                Options are not supported.
            </p>
        );
    return <LoadComparison settings={settings} duration={duration.data} onUseRecentWindow={onUseRecentWindow} />;
}

function LoadComparison({
    settings,
    duration,
    onUseRecentWindow
}: MicrotrendComparisonPanelProps & { duration: MicrotrendComparisonSnapshot['duration'] }) {
    const query = useAppQuery({
        ...trpc.entity.loadMicrotrendComparisonSnapshot.queryOptions({
            entityId: settings.entity.id,
            entityType: settings.entity.entityType,
            duration,
            startDateTime: settings.startDateTime,
            endDateTime: settings.endDateTime,
            shouldRemoveAfterHours: settings.shouldRemoveAfterHours
        }),
        staleTime: Infinity,
        gcTime: 0,
        retry: false,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        refetchOnMount: 'always'
    });
    return (
        <section className="rounded border border-zinc-700 bg-[#111827] p-4 text-zinc-100" aria-label="Microtrend comparison research">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">Microtrend comparison · Research</h2>
                <div className="flex flex-wrap gap-2">
                    <button type="button" className={controlClass} disabled={!onUseRecentWindow} onClick={onUseRecentWindow}>
                        {duration === '1d'
                            ? 'Use latest 400 chart days'
                            : duration === '60m'
                              ? 'Use latest 60 chart days'
                              : settings.entity.entityType === 'Cryptocurrency'
                                ? 'Use latest chart day'
                                : 'Use latest 2 chart days'}
                    </button>
                    <button
                        className={controlClass}
                        disabled={query.isFetching}
                        onClick={() => {
                            void query.refetch();
                        }}
                    >
                        Reload bars
                    </button>
                </div>
            </div>
            <p className="my-2 text-sm text-zinc-400">
                Frozen chart bars. Both detectors see the same replay prefix. Other overlays and auto-refresh are excluded here. Maximum
                2,000 bars; larger date windows are allowed for hourly through daily research.
            </p>
            <p className="mb-2 text-xs text-zinc-400">
                For a quick comparison, use the latest chart days above. This explicitly changes the date range in Settings below; the
                2,000-bar safety limit still applies.
            </p>
            {query.isFetching ? (
                <p role="status">Loading frozen bars…</p>
            ) : query.error ? (
                <p role="alert" className="text-red-300">
                    {query.error.message}
                </p>
            ) : query.data ? (
                <ReplayComparison key={query.dataUpdatedAt} snapshot={query.data} />
            ) : null}
        </section>
    );
}

function ReplayComparison({ snapshot }: { snapshot: MicrotrendComparisonSnapshot }) {
    const [cutoffCount, setCutoffCount] = useState(snapshot.bars.length);
    const [mode, setMode] = useState<MicrotrendComparisonMode>('both');
    const [config, setConfig] = useState(initialConfig);
    const [draft, setDraft] = useState(initialConfig);
    const [configError, setConfigError] = useState('');
    const [persistedRunDraft, setPersistedRunDraft] = useState('');
    const [persistedRunId, setPersistedRunId] = useState('');
    const [persistedRunError, setPersistedRunError] = useState('');
    const supportsStoredDaily = snapshot.entityType === 'Asset' && snapshot.duration === '1d' && snapshot.shouldRemoveAfterHours;
    const [showFibonacci, setShowFibonacci] = useState(true);
    const [answer, setAnswer] = useState<{
        config: MicrotrendComparisonConfig;
        persistedRunId: string;
        result: MicrotrendComparisonResult;
    } | null>(null);
    const [error, setError] = useState('');
    const [selectedId, setSelectedId] = useState<string | null>(null);
    // Clear stale output during render, before effects fire: no future segments or metrics can flash on rewind.
    const result = currentMicrotrendComparisonResult(answer, config, cutoffCount, persistedRunId);
    const bars = useMemo(() => snapshot.bars.slice(0, cutoffCount), [snapshot, cutoffCount]);
    const allBarCloses = useMemo(() => snapshot.bars.map((bar) => microtrendComparisonBarClose(snapshot, bar.t)), [snapshot]);
    const barCloses = useMemo(() => allBarCloses.slice(0, cutoffCount), [allBarCloses, cutoffCount]);
    const rows = useMemo(() => microtrendComparisonRows(result, mode, Boolean(result?.persisted)), [result, mode]);
    const selected = rows.find((row) => row.id === selectedId) ?? [...rows].reverse().find((row) => row.persistedImpulse);

    useEffect(() => {
        const controller = new AbortController();
        setError('');
        const timer = setTimeout(() => {
            void trpcUnbatchedClient.entity.compareMicrotrendBars
                .mutate(
                    {
                        snapshot,
                        cutoffCount,
                        config,
                        ...(persistedRunId === 'daily' ? { persistedDaily: true } : { persistedRunId: persistedRunId || undefined })
                    },
                    { signal: controller.signal }
                )
                .then(
                    (response) => {
                        if (!controller.signal.aborted) setAnswer({ config, persistedRunId, result: response });
                    },
                    (failure) => {
                        if (!controller.signal.aborted) setError(failure instanceof Error ? failure.message : 'Comparison failed');
                    }
                );
        }, 180);
        return () => {
            clearTimeout(timer);
            controller.abort();
        };
    }, [snapshot, cutoffCount, config, persistedRunId]);

    return (
        <>
            <div role="group" aria-label="Rust sensitivity" className="mt-3 flex flex-wrap items-center gap-2">
                <span className="text-sm">Rust sensitivity</span>
                {MICROTREND_RESEARCH_PRESETS.map((preset) => {
                    const active =
                        config.atrPeriod === preset.config.atrPeriod &&
                        config.reversalAtrMultiple === preset.config.reversalAtrMultiple &&
                        config.minimumReversalPct === preset.config.minimumReversalPct;
                    return (
                        <button
                            key={preset.id}
                            type="button"
                            aria-pressed={active}
                            className={`rounded border px-3 py-2 text-sm text-white ${active ? 'border-sky-400 bg-sky-950' : 'border-zinc-600 bg-zinc-900'}`}
                            onClick={() => {
                                const next = { ...preset.config };
                                setDraft(next);
                                setConfig(next);
                                setConfigError('');
                            }}
                        >
                            {preset.label}
                        </button>
                    );
                })}
                <span className="text-xs text-zinc-400">Experimental · {MICROTREND_RESEARCH_PRESET_VERSION}</span>
            </div>
            <p className="mt-2 text-xs text-zinc-400">
                Fine retains smaller swings; Coarse tolerates larger pullbacks. Each uses the same completed candles. These presets are not
                impulse labels, and their moves need not nest exactly. Numeric settings below remain available for custom research.
            </p>
            <div className="my-3 flex flex-wrap items-end gap-3">
                <label className="text-sm">
                    Show
                    <select
                        aria-label="Comparison detector"
                        className={`ml-2 ${controlClass}`}
                        value={mode}
                        onChange={(event) => {
                            const value = event.target.value;
                            if (value === 'both' || value === 'rust' || value === 'typescript') setMode(value);
                        }}
                    >
                        <option value="both">Both</option>
                        <option value="rust">Rust V2</option>
                        <option value="typescript">TypeScript V1</option>
                    </select>
                </label>
                {result?.persistedDailyAvailability ? (
                    <p role="status" className="text-sm">
                        Stored daily history: {result.persistedDailyAvailability.status}. {result.persistedDailyAvailability.reason}
                    </p>
                ) : null}
                <form
                    className="flex flex-wrap items-end gap-2"
                    onSubmit={(event) => {
                        event.preventDefault();
                        const parsed = microtrendComparisonConfigSchema.safeParse(draft);
                        if (!parsed.success) {
                            setConfigError(
                                'Use ATR period 1–200, ATR multiple 0–20, and reversal percentage greater than 0 and less than 100.'
                            );
                            return;
                        }
                        setConfigError('');
                        setConfig(parsed.data);
                    }}
                >
                    <label className="text-xs">
                        ATR period
                        <input
                            aria-label="ATR period"
                            type="number"
                            min="1"
                            max="200"
                            step="1"
                            className={`block w-24 ${controlClass}`}
                            value={Number.isFinite(draft.atrPeriod) ? draft.atrPeriod : ''}
                            onChange={(e) => setDraft({ ...draft, atrPeriod: e.target.valueAsNumber })}
                        />
                    </label>
                    <label className="text-xs">
                        ATR reversal ×
                        <input
                            aria-label="ATR reversal multiple"
                            type="number"
                            min="0"
                            max="20"
                            step="0.1"
                            className={`block w-28 ${controlClass}`}
                            value={Number.isFinite(draft.reversalAtrMultiple) ? draft.reversalAtrMultiple : ''}
                            onChange={(e) => setDraft({ ...draft, reversalAtrMultiple: e.target.valueAsNumber })}
                        />
                    </label>
                    <label className="text-xs">
                        Reversal floor %
                        <input
                            aria-label="Reversal floor percent"
                            type="number"
                            min="0"
                            max="100"
                            step="any"
                            className={`block w-28 ${controlClass}`}
                            value={Number.isFinite(draft.minimumReversalPct) ? draft.minimumReversalPct : ''}
                            onChange={(e) => setDraft({ ...draft, minimumReversalPct: e.target.valueAsNumber })}
                        />
                    </label>
                    <button type="submit" className={controlClass}>
                        Apply Rust settings
                    </button>
                </form>
            </div>
            {configError && (
                <p role="alert" className="text-red-300">
                    {configError}
                </p>
            )}
            <form
                className="mb-3 flex flex-wrap items-end gap-2 rounded border border-zinc-700 p-3"
                onSubmit={(event) => {
                    event.preventDefault();
                    const runId = persistedRunDraft.trim().toLowerCase();
                    if (!/^[a-f0-9]{64}$/.test(runId)) {
                        setPersistedRunError('Enter the 64-character run ID printed by the local Rust materializer.');
                        return;
                    }
                    setPersistedRunError('');
                    setPersistedRunId(runId);
                }}
            >
                {supportsStoredDaily ? (
                    <button
                        type="button"
                        className={controlClass}
                        onClick={() => {
                            const standard = { atrPeriod: 14, reversalAtrMultiple: 1, minimumReversalPct: 0.15 };
                            setDraft(standard);
                            setConfig(standard);
                            setConfigError('');
                            setPersistedRunError('');
                            setPersistedRunId('daily');
                        }}
                    >
                        Load stored daily history
                    </button>
                ) : (
                    <>
                        <label className="min-w-64 flex-1 text-xs">
                            Local canary run ID
                            <input
                                aria-label="Persisted Rust run ID"
                                autoComplete="off"
                                className={`block w-full font-mono ${controlClass}`}
                                value={persistedRunDraft}
                                onChange={(event) => setPersistedRunDraft(event.target.value)}
                                placeholder="64-character local materializer run ID"
                            />
                        </label>
                        <button type="submit" className={controlClass}>
                            Load persisted impulses
                        </button>
                    </>
                )}
                <button
                    type="button"
                    className={controlClass}
                    disabled={!persistedRunId}
                    onClick={() => {
                        setPersistedRunDraft('');
                        setPersistedRunId('');
                        setPersistedRunError('');
                    }}
                >
                    Clear
                </button>
                <label className="flex items-center gap-2 text-sm">
                    <input type="checkbox" checked={showFibonacci} onChange={(event) => setShowFibonacci(event.target.checked)} />
                    Selected impulse Fibonacci
                </label>
                <p className="basis-full text-xs text-zinc-400">
                    {supportsStoredDaily
                        ? 'Read-only published daily history for this symbol, using Standard settings and the replay cutoff. Missing or delayed publication is shown explicitly. No trading admission.'
                        : 'Optional local canary view. Reads the named run at the replay cutoff and rejects mismatched entities, timeframes, settings or anchors.'}
                </p>
                {persistedRunError && (
                    <p role="alert" className="basis-full text-red-300">
                        {persistedRunError}
                    </p>
                )}
            </form>
            <div className="flex flex-wrap items-center gap-2">
                <button
                    aria-label="Previous replay bar"
                    className={controlClass}
                    disabled={cutoffCount === 1}
                    onClick={() => setCutoffCount((c) => c - 1)}
                >
                    ← Bar
                </button>
                <button
                    aria-label="Next replay bar"
                    className={controlClass}
                    disabled={cutoffCount === snapshot.bars.length}
                    onClick={() => setCutoffCount((c) => c + 1)}
                >
                    Bar →
                </button>
                <button
                    className={controlClass}
                    disabled={cutoffCount === snapshot.bars.length}
                    onClick={() => setCutoffCount(snapshot.bars.length)}
                >
                    Latest
                </button>
                <label className="flex min-w-48 flex-1 items-center gap-2 text-sm">
                    Replay
                    <input
                        aria-label="Replay cutoff"
                        className="w-full"
                        type="range"
                        min="1"
                        max={snapshot.bars.length}
                        value={cutoffCount}
                        onInput={(e) => setCutoffCount(e.currentTarget.valueAsNumber)}
                    />
                </label>
                <span className="text-sm tabular-nums">
                    {cutoffCount} / {snapshot.bars.length} bars
                </span>
            </div>
            <p className="my-2 text-sm">As of {timeLabel(barCloses.at(-1)!)} ET · completed bars only</p>
            <div aria-label="Microtrend color legend" className="mb-2 flex flex-wrap gap-x-6 gap-y-1 text-sm">
                <p>
                    Rust: <span style={{ color: '#38bdf8' }}>━ Up</span> / <span style={{ color: '#fb923c' }}>━ Down</span>
                </p>
                <p>
                    TypeScript: <span style={{ color: '#00ff42' }}>━ Up</span> / <span style={{ color: '#ff00de' }}>━ Down</span> /{' '}
                    <span style={{ color: '#f0ff00' }}>━ Sideways</span>
                </p>
                {result?.persisted && (
                    <p>
                        Persisted composite: <span style={{ color: '#a78bfa' }}>━ Up</span> /{' '}
                        <span style={{ color: '#f472b6' }}>━ Down</span>
                    </p>
                )}
            </div>
            <p className="mb-2 text-xs text-zinc-400">
                Rust: solid confirmed · dashed developing · dotted interrupted. C arrows mark confirmation, not entries. TypeScript uses
                candle-body midpoints. Persisted composites are solid when finalized and dashed while provisional; F/P arrows show when that
                lifecycle became usable.
            </p>
            <ComparisonChart
                bars={bars}
                rows={rows}
                fibonacciImpulse={showFibonacci ? selected?.persistedImpulse : undefined}
                barCloses={barCloses}
                onSelect={setSelectedId}
            />
            {result && mode !== 'typescript' && (
                <MicrotrendObservationPanel rust={result.rust} selectedLegId={selected?.rustLeg?.id} onReplay={setCutoffCount} />
            )}
            <div role="status" aria-live="polite" className="my-2 text-sm">
                {error ? (
                    <span className="text-red-300">{error}</span>
                ) : result ? (
                    `${result.rust.confirmed.length} Rust confirmed · ${result.rust.interrupted.length} interrupted · ${result.rust.state.kind} · ${result.legacy.length} TypeScript legs · ${result.persisted?.impulses.length ?? 0} persisted composites · ${result.rust.diagnostics.length} diagnostics`
                ) : (
                    'Calculating this replay prefix…'
                )}
            </div>
            <p className="text-xs text-zinc-400">
                Historical availability is assumed at bar close; original arrival times and adjustment history are not verified. V1 is
                recalculated at every cutoff and can repaint. Rust needs {config.atrPeriod} warmup bars. These labels do not establish
                trading performance or impulse quality.
            </p>
            {result && (
                <p className="my-2 break-all font-mono text-xs text-zinc-500">
                    {result.rust.algorithmVersion} · snapshot {result.snapshotId.slice(0, 16)} · run{' '}
                    {result.rust.runFingerprint.slice(0, 16)}
                </p>
            )}
            {result?.persisted && (
                <p className="my-2 break-all font-mono text-xs text-violet-300">
                    persisted {result.persisted.runId.slice(0, 16)} · {result.persisted.run.continuityPolicy} · revision{' '}
                    {result.persisted.publication.revision} · covered through {timeLabel(result.persisted.publication.coveredThrough)} ET
                </p>
            )}
            <div className="mt-3 grid gap-4 lg:grid-cols-2">
                <div className="max-h-64 overflow-auto rounded border border-zinc-700">
                    <table className="w-full text-left text-xs">
                        <caption className="p-2 text-left text-zinc-400">Select a segment on the chart or a row to inspect it.</caption>
                        <thead>
                            <tr>
                                <th className="p-2">Detector / state</th>
                                <th className="p-2">Start (ET)</th>
                                <th className="p-2">Move %</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((row) => (
                                <tr key={row.id} className={selected?.id === row.id ? 'bg-zinc-700' : ''}>
                                    <td className="p-2">
                                        <button
                                            className="text-left underline"
                                            style={{ color: row.color }}
                                            onClick={() => setSelectedId(row.id)}
                                        >
                                            {row.source} {row.direction} · {row.status}
                                        </button>
                                    </td>
                                    <td className="p-2">{row.points[0] ? timeLabel(row.points[0].time) : '—'}</td>
                                    <td className="p-2">
                                        {numberLabel(
                                            row.rustLeg?.metrics.priceChangePct ??
                                                row.persistedImpulse?.metrics.priceChangePct ??
                                                row.reportedPct
                                        )}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
                <SegmentDetails row={selected} />
            </div>
            {result && result.rust.diagnostics.length > 0 && (
                <details className="mt-3 text-xs">
                    <summary>Rust diagnostics for this prefix</summary>
                    <ul className="max-h-40 overflow-auto">
                        {result.rust.diagnostics.map((item, i) => (
                            <li key={`${item.sequence}:${i}`}>
                                Bar {item.sequence + 1}: {item.diagnostic}
                            </li>
                        ))}
                    </ul>
                </details>
            )}
        </>
    );
}

function ComparisonChart({
    bars,
    rows,
    fibonacciImpulse,
    barCloses,
    onSelect
}: {
    bars: MicrotrendComparisonSnapshot['bars'];
    rows: MicrotrendComparisonRow[];
    fibonacciImpulse: MicrotrendPersistedResearchImpulse | undefined;
    barCloses: number[];
    onSelect: (id: string) => void;
}) {
    const container = useRef<HTMLDivElement>(null);
    useLayoutEffect(() => {
        if (!container.current) return;
        const chart = createChart(container.current, {
            autoSize: true,
            height: 420,
            layout: { background: { type: ColorType.Solid, color: '#0b1020' }, textColor: '#cbd5e1' },
            grid: { vertLines: { color: '#1e293b' }, horzLines: { color: '#1e293b' } },
            localization: { timeFormatter: (time: Time) => (typeof time === 'number' ? timeLabel(time) : String(time)) },
            timeScale: {
                timeVisible: true,
                secondsVisible: false,
                tickMarkFormatter: (time: Time) => (typeof time === 'number' ? timeLabel(time).slice(5, 16) : String(time))
            }
        });
        const candles = chart.addSeries(CandlestickSeries, {
            upColor: '#4b8071',
            downColor: '#925c6a',
            borderVisible: false,
            wickUpColor: '#4b8071',
            wickDownColor: '#925c6a',
            priceLineVisible: false
        });
        candles.setData(bars.map((b) => ({ time: chartTime(b.t), open: b.o, high: b.h, low: b.l, close: b.c })));
        const seriesRows = new Map<ISeriesApi<SeriesType, Time>, string>();
        for (const row of rows) {
            const line = chart.addSeries(LineSeries, {
                color: row.color,
                lineWidth: row.source === 'Persisted composite' ? 4 : row.source === 'Rust' ? 3 : 2,
                lineStyle:
                    row.status === 'developing' || row.status === 'provisional'
                        ? LineStyle.Dashed
                        : row.status === 'interrupted'
                          ? LineStyle.Dotted
                          : LineStyle.Solid,
                lastValueVisible: false,
                priceLineVisible: false,
                crosshairMarkerVisible: true,
                pointMarkersVisible: row.points.length === 1
            });
            line.setData(row.points.map((p) => ({ time: chartTime(p.time), value: p.value })));
            seriesRows.set(line, row.id);
        }
        if (fibonacciImpulse) {
            const firstVisible = bars.find((bar) => bar.t >= fibonacciImpulse.availableAt)?.t;
            const lastVisible = bars.at(-1)?.t;
            if (firstVisible !== undefined && lastVisible !== undefined)
                for (const [index, level] of fibonacciImpulse.fibonacci.entries()) {
                    const line = chart.addSeries(LineSeries, {
                        title: `Fib ${(level.retracementRatio * 100).toFixed(1)}%`,
                        color: index === 0 || index === fibonacciImpulse.fibonacci.length - 1 ? '#c4b5fd' : 'rgba(196,181,253,0.55)',
                        lineWidth: index === 0 || index === fibonacciImpulse.fibonacci.length - 1 ? 2 : 1,
                        lineStyle: LineStyle.Dashed,
                        lastValueVisible: true,
                        priceLineVisible: false,
                        crosshairMarkerVisible: false,
                        pointMarkersVisible: firstVisible === lastVisible
                    });
                    line.setData(
                        firstVisible === lastVisible
                            ? [{ time: chartTime(firstVisible), value: level.price }]
                            : [
                                  { time: chartTime(firstVisible), value: level.price },
                                  { time: chartTime(lastVisible), value: level.price }
                              ]
                    );
                    seriesRows.set(line, `persisted:${fibonacciImpulse.id}`);
                }
        }
        const markerForAvailability = (row: MicrotrendComparisonRow) => {
            if (!row.persistedImpulse) return null;
            let barIndex = -1;
            for (let index = 0; index < bars.length; index++) {
                if (barCloses[index]! <= row.persistedImpulse.availableAt) barIndex = index;
                else break;
            }
            const bar = bars[barIndex];
            return bar
                ? {
                      time: chartTime(bar.t),
                      position: 'aboveBar' as const,
                      shape: 'arrowDown' as const,
                      color: row.color,
                      text: row.persistedImpulse.lifecycle === 'finalized' ? 'F' : 'P',
                      id: row.id
                  }
                : null;
        };
        const markers = createSeriesMarkers(
            candles,
            rows
                .flatMap((row) => {
                    if (row.persistedImpulse) return [markerForAvailability(row)];
                    if (row.confirmationSequence === undefined) return [];
                    const bar = bars[row.confirmationSequence!];
                    return [
                        bar
                            ? {
                                  time: chartTime(bar.t),
                                  position: 'aboveBar' as const,
                                  shape: 'arrowDown' as const,
                                  color: row.color,
                                  text: 'C',
                                  id: row.id
                              }
                            : null
                    ];
                })
                .filter((marker) => marker !== null)
                .sort((a, b) => Number(a.time) - Number(b.time))
        );
        chart.subscribeClick((event) => {
            const id = event.hoveredObjectId ?? (event.hoveredSeries && seriesRows.get(event.hoveredSeries));
            if (typeof id === 'string') onSelect(id);
        });
        chart.timeScale().fitContent();
        return () => {
            markers.detach();
            chart.remove();
        };
    }, [bars, rows, fibonacciImpulse, barCloses, onSelect]);
    return <div ref={container} className="h-[420px] w-full" aria-label="Candles and microtrend segments through the selected cutoff" />;
}

function SegmentDetails({ row }: { row?: MicrotrendComparisonRow }) {
    if (!row)
        return (
            <p className="p-3 text-sm text-zinc-400">
                Select a segment to see its anchors, percentage change, momentum, and confirmation delay.
            </p>
        );
    const leg = row.rustLeg;
    const composite = row.persistedImpulse;
    const metrics = leg?.metrics;
    const entries: [string, string][] = [
        ['Detector / state', `${row.source} ${row.direction} · ${row.status}`],
        ['Start (ET)', row.points[0] ? timeLabel(row.points[0].time) : 'Unavailable'],
        ['End (ET)', row.points.at(-1) ? timeLabel(row.points.at(-1)!.time) : 'Unavailable'],
        ['Start → end price', `${numberLabel(row.points[0]?.value, 4)} → ${numberLabel(row.points.at(-1)?.value, 4)}`],
        [
            leg || composite ? 'Anchor return %' : 'V1 reported change %',
            numberLabel(metrics?.priceChangePct ?? composite?.metrics.priceChangePct ?? row.reportedPct)
        ],
        ['Included bars', String(row.barCount)]
    ];
    if (leg && metrics)
        entries.push(
            ['Anchor basis', `${leg.origin.basis} → ${leg.endpoint.basis}`],
            ['First detected (ET)', timeLabel(leg.firstDetected.availableAt)],
            ['Confirmed (ET)', row.confirmedAt === undefined ? 'Not confirmed' : timeLabel(row.confirmedAt)],
            ['Confirmation delay', row.delayBars === undefined ? 'Not confirmed' : `${row.delayBars} bars / ${row.delaySeconds} seconds`],
            ['Elapsed move', `${metrics.elapsedSeconds} seconds / ${metrics.barIntervals} intervals`],
            ['Return % / bar', numberLabel(metrics.returnPctPerBar)],
            ['Return % / elapsed minute', numberLabel(metrics.returnPctPerElapsedMinute)],
            ['Displacement / ATR', numberLabel(metrics.displacementAtr)],
            ['Speed ATR / bar', numberLabel(metrics.speedAtrPerBar)],
            ['Directional close-path efficiency', numberLabel(metrics.directionalClosePathEfficiency)],
            ['Max internal close pullback', numberLabel(metrics.maxInternalClosePullback)],
            ['Move volume / mean', `${numberLabel(metrics.volumeSum, 0)} / ${numberLabel(metrics.volumeMean, 0)}`]
        );
    if (composite)
        entries.push(
            ['Anchor basis', `${composite.origin.basis} → ${composite.endpoint.basis}`],
            ['Geometry first known (ET)', timeLabel(composite.geometryFirstKnownAt)],
            [composite.lifecycle === 'finalized' ? 'Immutable from (ET)' : 'Provisional from (ET)', timeLabel(composite.availableAt)],
            ['Finalized (ET)', composite.finalizedAt === null ? 'Not finalized' : timeLabel(composite.finalizedAt)],
            ['Finalization reason', composite.finalizationReason ?? 'Still provisional'],
            ['Constituent base legs', String(composite.constituentLegIds.length)],
            ['Elapsed move', `${composite.metrics.elapsedSeconds} seconds / ${composite.metrics.barIntervals} intervals`],
            ['Displacement / ATR', numberLabel(composite.metrics.displacementAtr)],
            ['Speed ATR / bar', numberLabel(composite.metrics.speedAtrPerBar)],
            ['Maximum internal retracement', numberLabel(composite.metrics.maximumInternalRetracementFraction)],
            ['Directional close-path efficiency', numberLabel(composite.path?.directionalClosePathEfficiency)],
            ['Largest bar / displacement', numberLabel(composite.path?.maximumBarRangeFraction)],
            ['Largest gap / displacement', numberLabel(composite.path?.maximumOpenGapFraction)],
            [
                'Fibonacci prices',
                composite.fibonacci
                    .map((level) => `${(level.retracementRatio * 100).toFixed(1)}% ${numberLabel(level.price, 4)}`)
                    .join(' · ')
            ]
        );
    if (row.currentPullback !== undefined)
        entries.push(
            ['Current close pullback', numberLabel(row.currentPullback)],
            ['Current retracement fraction', numberLabel(row.currentRetracement)],
            ['Bars since extreme', String(row.barsSinceExtreme)]
        );
    if (row.interruption) entries.push(['Interrupted by', row.interruption]);
    return (
        <div className="max-h-80 overflow-auto rounded border border-zinc-700 p-3">
            <dl className="grid grid-cols-2 gap-2 text-xs">
                {entries.map(([label, value]) => (
                    <div key={label} className="contents">
                        <dt className="text-zinc-400">{label}</dt>
                        <dd className="break-words">{value}</dd>
                    </div>
                ))}
            </dl>
            {row.source === 'TypeScript' && (
                <p className="mt-3 text-xs text-amber-200">
                    V1 has no causal confirmation time. Its reported return uses a different price basis than the midpoint line, so it is
                    not directly comparable to the Rust anchor return.
                </p>
            )}
            {composite?.lifecycle === 'provisional' && (
                <p className="mt-3 text-xs text-amber-200">
                    This geometry is the point-in-time provisional state. Later completed bars may extend, replace, or finalize it.
                </p>
            )}
        </div>
    );
}
